import React from 'react'
import { ReportData } from '@/types'
import { getDepartmentConfig } from '@/services/storage'

interface ReportTemplateProps {
  data: ReportData
}

export const ReportTemplate: React.FC<ReportTemplateProps> = ({ data }) => {
  const deptConfig = getDepartmentConfig() || { cityName: '', departmentName: '城市管理局' }
  return (
    <div className="bg-white p-6 max-w-4xl mx-auto" style={{ fontFamily: 'SimSun, serif' }}>
      {/* 报告标题 */}
      <div className="text-center border-b-4 border-double pb-4 mb-6">
        <h1 className="text-3xl font-bold text-red-700 tracking-wider mb-2">
          {deptConfig.cityName}燃气安全检查报告
        </h1>
        <p className="text-sm text-gray-500">报告编号：{data.reportNumber}</p>
      </div>

      {/* 基本信息 */}
      <div className="bg-gray-50 p-4 rounded mb-6">
        <table className="w-full">
          <tbody>
            <tr className="border-b border-gray-200">
              <td className="py-2 text-gray-600 w-28">检查类型：</td>
              <td className="py-2 font-medium">{data.checkType}</td>
            </tr>
            <tr className="border-b border-gray-200">
              <td className="py-2 text-gray-600">被检查单位：</td>
              <td className="py-2 font-medium">{data.inspectedUnit}</td>
            </tr>
            <tr className="border-b border-gray-200">
              <td className="py-2 text-gray-600">检查地址：</td>
              <td className="py-2 font-medium">{data.inspectedAddress}</td>
            </tr>
            <tr className="border-b border-gray-200">
              <td className="py-2 text-gray-600">检查人员：</td>
              <td className="py-2 font-medium">{data.inspector}</td>
            </tr>
            <tr>
              <td className="py-2 text-gray-600">检查日期：</td>
              <td className="py-2 font-medium">{data.checkDate}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 一、检查概况 */}
      <section className="mb-6">
        <h2 className="text-lg font-bold text-red-700 mb-3">一、检查概况</h2>
        <p className="text-gray-700 leading-relaxed indent-8">{data.overview}</p>
      </section>

      {/* 二、发现的主要问题 */}
      <section className="mb-6">
        <h2 className="text-lg font-bold text-red-700 mb-3">二、发现的主要问题</h2>

        {/* 重大隐患 */}
        {data.majorHazards.length > 0 && (
          <div className="mb-4 pl-4 border-l-4 border-red-600 bg-red-50 p-3 rounded-r">
            <h3 className="font-semibold text-red-700 mb-2">
              （一）重大隐患（{data.majorHazards.length}项）
            </h3>
            <div className="text-gray-700 space-y-1">
              {data.majorHazards.map((hazard, i) => (
                <p key={i}>
                  {i + 1}. {hazard}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* 一般隐患 */}
        {data.generalHazards.length > 0 && (
          <div className="pl-4 border-l-4 border-orange-500 bg-orange-50 p-3 rounded-r">
            <h3 className="font-semibold text-orange-600 mb-2">
              （二）一般隐患（{data.generalHazards.length}项）
            </h3>
            <div className="text-gray-700 space-y-1">
              {data.generalHazards.map((hazard, i) => (
                <p key={i}>
                  {i + 1}. {hazard}
                </p>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* 三、整改建议 */}
      <section className="mb-6">
        <h2 className="text-lg font-bold text-red-700 mb-3">三、整改建议</h2>
        <div className="text-gray-700 space-y-2">
          {data.suggestions.map((suggestion, i) => (
            <p key={i} className="indent-8">
              {i + 1}. {suggestion}
            </p>
          ))}
        </div>
      </section>

      {/* 签名区域 */}
      <section className="mt-8 pt-6 border-t border-gray-300">
        <div className="grid grid-cols-2 gap-8">
          <div>
            <div className="font-medium text-gray-800 mb-2">检查人员签名：</div>
            <div className="h-20 border border-gray-300 rounded bg-gray-50 flex items-center justify-center">
              {data.inspectorSignature ? (
                <img
                  src={data.inspectorSignature}
                  alt="检查人员签名"
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <span className="text-gray-400 text-sm">未签名</span>
              )}
            </div>
            <div className="text-sm text-gray-500 mt-1">日期：{data.checkDate}</div>
          </div>
          <div>
            <div className="font-medium text-gray-800 mb-2">被检查单位代表签名：</div>
            <div className="h-20 border border-gray-300 rounded bg-gray-50 flex items-center justify-center">
              {data.unitRepresentativeSignature ? (
                <img
                  src={data.unitRepresentativeSignature}
                  alt="单位代表签名"
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <span className="text-gray-400 text-sm">未签名</span>
              )}
            </div>
            <div className="text-sm text-gray-500 mt-1">日期：{data.checkDate}</div>
          </div>
        </div>
      </section>

      {/* 页脚 */}
      <div className="text-center text-sm text-gray-400 mt-8 pt-4 border-t border-gray-200">
        <p>{deptConfig.departmentName}</p>
        <p>报告生成日期：{data.checkDate}</p>
      </div>
    </div>
  )
}
